import React, { useEffect, useState } from 'react';
import { IconButton, InputAdornment, Popover, TextField } from '@mui/material';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { parseIsoDate } from 'utils/patientBirthInput';
import styles from './styles.module.css';

const BIRTH_MODES = [
  { value: 'dateOfBirth', labelKey: 'birth-mode-date-of-birth' },
  { value: 'estimatedAge', labelKey: 'birth-mode-estimated-age' },
];

function isUsLocale(language) {
  return String(language || '').toLowerCase().startsWith('en');
}

function maskDate(value) {
  const raw = String(value ?? '').replace(/[^\d/]/g, '');
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  const slash = raw.endsWith('/');

  if (digits.length <= 2) return slash && digits.length === 2 ? `${digits}/` : digits;
  if (digits.length <= 4) {
    const masked = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return slash && digits.length === 4 ? `${masked}/` : masked;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function toIso(masked, isUS) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(masked || '').trim());
  if (!match) return '';
  const iso = `${match[3]}-${isUS ? match[1] : match[2]}-${isUS ? match[2] : match[1]}`;
  return parseIsoDate(iso) ? iso : '';
}

function fromIso(iso, isUS) {
  const date = parseIsoDate(iso);
  if (!date) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  return isUS ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
}

function BirthModeSelector({ value, disabled, onChange, required }) {
  const { t } = useTranslation();
  const selectedMode = value === 'estimatedAge' ? 'estimatedAge' : 'dateOfBirth';

  return (
    <div
      className={styles.BirthModeSelector}
      role="radiogroup"
      aria-label={t('physio.physio-edit-patient-dialog.birth-input-mode')}
    >
      <div className={styles.BirthModeOptions}>
        {BIRTH_MODES.map((mode) => {
          const selected = selectedMode === mode.value;
          return (
            <button
              key={mode.value}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`${styles.BirthModeOption} ${selected ? styles.BirthModeOptionSelected : ''}`}
              disabled={disabled}
              onClick={() => {
                if (disabled || mode.value === selectedMode) return;
                onChange({ target: { value: mode.value } });
              }}
            >
              <span className={styles.BirthModeRadio} aria-hidden="true">
                {selected ? <span className={styles.BirthModeRadioInner} /> : null}
              </span>
              {t(`physio.physio-edit-patient-dialog.${mode.labelKey}`)}
            </button>
          );
        })}
        {required ? <span className={styles.BirthModeRequired}>*</span> : null}
      </div>
    </div>
  );
}

function BirthDateField({ value, onChange, error, helperText, disabled, label, margin = 'none', required }) {
  const { i18n } = useTranslation();
  const isUS = isUsLocale(i18n.language);
  const [display, setDisplay] = useState(() => fromIso(value, isUS));
  const [calendarAnchor, setCalendarAnchor] = useState(null);

  useEffect(() => {
    setDisplay(fromIso(value, isUS));
  }, [value, isUS]);

  const emitIso = (iso) => {
    onChange({ target: { name: 'birth_date', value: iso } });
  };

  const handleTextChange = (event) => {
    const masked = maskDate(event.target.value);
    setDisplay(masked);
    if (!masked) {
      emitIso('');
      return;
    }
    if (masked.length === 10) emitIso(toIso(masked, isUS));
  };

  const handleCalendarChange = (date) => {
    if (!date || Number.isNaN(date.getTime())) {
      emitIso('');
      setDisplay('');
      setCalendarAnchor(null);
      return;
    }

    const iso = format(date, 'yyyy-MM-dd');
    setDisplay(fromIso(iso, isUS));
    emitIso(iso);
    setCalendarAnchor(null);
  };

  return (
    <div className={styles.BirthDateField}>
      <TextField
        fullWidth
        margin={margin}
        label={label}
        name="birth_date"
        value={display}
        onChange={handleTextChange}
        placeholder={isUS ? '12/31/1999' : '31/12/1999'}
        error={Boolean(error)}
        helperText={helperText}
        required={required}
        disabled={disabled}
        slotProps={{
          htmlInput: { maxLength: 10, autoComplete: 'off' },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  aria-label="open-calendar"
                  disabled={disabled}
                  onClick={(event) => setCalendarAnchor(event.currentTarget)}
                >
                  <CalendarMonthOutlined fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      <Popover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor}
        onClose={() => setCalendarAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <DateCalendar
          value={parseIsoDate(value)}
          minDate={new Date(1901, 0, 1)}
          maxDate={new Date()}
          onChange={handleCalendarChange}
        />
      </Popover>
    </div>
  );
}

function BirthInputFields({
  allowEstimatedAge,
  allowBirthDate = true,
  required = true,
  birthInputMode,
  birthDate,
  estimatedAge,
  errors,
  isLoading,
  onChange,
  onModeChange,
}) {
  const { t } = useTranslation();
  const isEstimatedMode = birthInputMode === 'estimatedAge';
  const showModeSelector = allowBirthDate && allowEstimatedAge;

  const handleEstimatedAgeChange = (event) => {
    onChange({
      ...event,
      target: {
        ...event.target,
        name: 'estimated_age',
        value: String(event.target.value ?? '')
          .replace(/[^0-9]/g, '')
          .slice(0, 3),
      },
    });
  };

  if (!allowBirthDate) {
    return null;
  }

  const estimatedAgeField = (
    <TextField
      fullWidth
      margin={showModeSelector ? 'none' : 'normal'}
      type="text"
      inputMode="numeric"
      label={showModeSelector ? undefined : `${t('physio.physio-edit-patient-dialog.estimated-age')}`}
      name="estimated_age"
      value={estimatedAge}
      onChange={handleEstimatedAgeChange}
      error={Boolean(errors.estimated_age)}
      helperText={errors.estimated_age && t(`physio.physio-edit-patient-dialog.${errors.estimated_age}`)}
      required={required}
      disabled={isLoading}
      slotProps={{
        htmlInput: { maxLength: 3, inputMode: 'numeric' },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              {t('physio.physio-edit-patient-dialog.estimated-age-approx')}
            </InputAdornment>
          ),
        },
      }}
    />
  );

  const dateField = (
    <BirthDateField
      value={birthDate}
      onChange={onChange}
      error={errors.birth_date}
      helperText={errors.birth_date && t(`physio.physio-edit-patient-dialog.${errors.birth_date}`)}
      disabled={isLoading}
      label={showModeSelector ? undefined : `${t('physio.physio-edit-patient-dialog.birth-date')}`}
      margin={showModeSelector ? 'none' : 'normal'}
      required={required}
    />
  );

  if (!allowEstimatedAge) {
    return dateField;
  }

  return (
    <div className={styles.BirthInput}>
      <BirthModeSelector value={birthInputMode} disabled={isLoading} onChange={onModeChange} required={required} />
      {isEstimatedMode ? estimatedAgeField : dateField}
    </div>
  );
}

export default BirthInputFields;
